import type { LucideIcon } from "lucide-react";
import {
  Mic,
  Bot,
  ListChecks,
  Trophy,
  BookOpen,
  Headphones,
  BarChart3,
  Sparkles,
} from "lucide-react";

export const APP_NAME = "Lyder Talk";

export interface Step {
  number: number;
  icon: LucideIcon;
  title: string;
  description: string;
  imageSeed: string;
  caption: string;
}

export const STEPS: Step[] = [
  {
    number: 1,
    icon: BookOpen,
    title: "Pick a topic",
    description:
      "Browse curated topics or describe what you want to practice. The AI builds the lesson around you.",
    imageSeed: "lyder-talk-step-pick-topic",
    caption: "From climate action to small talk in cafes.",
  },
  {
    number: 2,
    icon: Headphones,
    title: "Practice out loud",
    description:
      "Read the prompt, record your answer, and listen back. The conversation moves at your pace.",
    imageSeed: "lyder-talk-step-record",
    caption: "Your voice, your pace, no judgment.",
  },
  {
    number: 3,
    icon: Bot,
    title: "Get feedback that helps",
    description:
      "See where your grammar slipped, what vocabulary to try, and how your pronunciation sounded.",
    imageSeed: "lyder-talk-step-feedback",
    caption: "Notes on what to keep and what to adjust.",
  },
  {
    number: 4,
    icon: BarChart3,
    title: "See the streak build",
    description:
      "Track sessions, words saved, and the days in a row you've shown up. Small wins, stacked.",
    imageSeed: "lyder-talk-step-progress",
    caption: "A clear line from today to fluency.",
  },
];

export interface Feature {
  title: string;
  description: string;
  icon: LucideIcon;
  tone: "primary" | "amber" | "neutral" | "success";
  span: "default" | "wide" | "tall" | "large";
  preview: "conversation" | "vocabulary" | "streak" | "questions";
}

export const FEATURES: Feature[] = [
  {
    title: "Real conversations on demand",
    description:
      "Pick a topic, get a question, answer with your voice. The thread continues like a chat, not a quiz.",
    icon: Mic,
    tone: "primary",
    span: "large",
    preview: "conversation",
  },
  {
    title: "Vocabulary that sticks",
    description:
      "Words show up in context, get saved to your deck, and surface again in later sessions.",
    icon: ListChecks,
    tone: "amber",
    span: "default",
    preview: "vocabulary",
  },
  {
    title: "Streaks and levels",
    description:
      "Show up daily, earn XP, level up. Gamification that respects your time.",
    icon: Trophy,
    tone: "success",
    span: "default",
    preview: "streak",
  },
  {
    title: "Questions tailored to you",
    description:
      "The AI adjusts difficulty, topic depth, and length to your stated level and recent sessions.",
    icon: Sparkles,
    tone: "neutral",
    span: "wide",
    preview: "questions",
  },
];

export const BENEFITS: string[] = [
  "Unlimited topics",
  "AI question generation",
  "Pronunciation notes",
  "Vocabulary review",
  "Practice history",
  "Streak tracking",
];

export interface TrustMark {
  name: string;
  initials: string;
}

export const TRUST_MARKS: TrustMark[] = [
  { name: "Hanoi Speaking Circle", initials: "HSC" },
  { name: "Berlin Cafe English", initials: "BCE" },
  { name: "Lisbon Language Hub", initials: "LLH" },
  { name: "Tokyo Conversation Lab", initials: "TCL" },
  { name: "Bogota Talk Group", initials: "BTG" },
  { name: "Warsaw Practice Pod", initials: "WPP" },
];

export interface SampleMessage {
  role: "ai" | "user" | "feedback";
  text: string;
  meta?: string;
}

export const SAMPLE_CONVERSATION: SampleMessage[] = [
  {
    role: "ai",
    text: "What small change could your city make this year to cut car trips in half?",
  },
  {
    role: "user",
    text: "Wider bike lanes and a bus lane on the main road, with the price of a monthly pass halved.",
    meta: "00:18",
  },
  {
    role: "feedback",
    text: "Clear structure and a concrete example. Try linking the two ideas with \"if... then\" next time.",
  },
];

/**
 * The practice loop's data (stages, copy, waveform) moved to the UI
 * package with the components that use it, so the auth showcase runs the
 * same loop as the landing page. Re-exported here for the landing files
 * that still import from this module.
 */
export {
  CREATE_TOPIC,
  LOOP_WAVEFORM,
  PRACTICE_LOOP,
  PROMPT_CHARS,
  SAMPLE_TOPIC,
} from "@lyder-talk/ui";
export type { LoopStage, LoopStageId } from "@lyder-talk/ui";

export const HERO_PRIMARY_CTA = "Start practicing free";
export const HERO_SECONDARY_CTA = "Sign in";

export const FINAL_CTA = {
  title: "Your next sentence is one tap away.",
  body: "Free while in beta. No card, no spam, no follow-up emails about our launch.",
  primary: "Start practicing free",
  secondary: "Read the FAQ",
  reassurance: "Cancel any time. Your data stays yours.",
};
